import React, { useState, useMemo, useEffect } from 'react';
import { Box, VStack, Text, Grid, Button, useToast, Flex, useBreakpointValue, Badge } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../../contexts/GameContext.tsx';
import { useSound } from '../../contexts/SoundContext.tsx';
import { useTranslation } from 'react-i18next';
import { shuffleAnswers, getOriginalIndex, ShuffledAnswer } from '../../utils/shuffleAnswers.ts';
import TimerBar from '../Common/TimerBar.tsx';
import PlayerName from '../Common/PlayerName.tsx';
import { auth } from '../../config/firebase.ts';

const QuestionCard: React.FC = () => {
  const { t } = useTranslation();
  const { state, answerQuestion, clearAnswerResult } = useGame();
  const { playSound } = useSound();
  const toast = useToast();
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  
  const buttonColumns = useBreakpointValue({ base: 1, md: 2 });
  const cardWidth = useBreakpointValue({ base: "90%", sm: "450px", md: "600px" });

  // Local question timer hooks must be declared before any early returns
  const enableQuestionTimer = !!state.advanced?.enableQuestionTimer;
  const questionTimerSeconds = Math.max(5, Math.min(120, state.advanced?.questionTimerSeconds || 20));
  const [questionDeadline, setQuestionDeadline] = useState<number | null>(null);

  useEffect(() => {
    if (!enableQuestionTimer) { setQuestionDeadline(null); return; }
    if (!state.currentQuestion) { setQuestionDeadline(null); return; }
    setQuestionDeadline(Date.now() + questionTimerSeconds * 1000);
  }, [state.currentQuestion?.id, enableQuestionTimer, questionTimerSeconds]);

  // On expiry, auto-answer (timeout) -> treat as wrong without a selected answer
  useEffect(() => {
    if (!enableQuestionTimer || !questionDeadline || state.answerResult) return;
    const id = window.setInterval(async () => {
      if (Date.now() >= questionDeadline) {
        window.clearInterval(id);
        if (!state.currentQuestion) return;
        const fallback = state.currentQuestion.options?.[0] || '';
        try {
          const ok = await answerQuestion(fallback, undefined);
          playSound(ok ? 'answerCorrect' : 'answerWrong');
        } catch {}
      }
    }, 200);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionDeadline, enableQuestionTimer, state.answerResult, state.currentQuestion?.id]);

  // Mélanger les réponses une seule fois quand la question change
  const shuffledAnswers = useMemo(() => {
    if (!state.currentQuestion?.options) return [];
    return shuffleAnswers(state.currentQuestion.options);
  }, [state.currentQuestion?.id, state.currentQuestion?.options]);

  // Fonction pour obtenir le style d'un bouton selon les résultats
  const getButtonStyle = (answerText: string) => {
    if (!state.answerResult) {
      // État normal - pas de résultat
      return {
        bg: "var(--gris-bleute)",
        color: "var(--texte-clair)",
        borderColor: "rgba(139, 148, 179, 0.3)",
        _hover: {
          bg: "var(--bleu-electrique)",
          borderColor: "var(--bleu-electrique)",
          transform: "translateY(-2px)",
        }
      };
    }

    // Comparer directement avec le texte de la réponse affichée
    const isCorrectAnswer = answerText === state.answerResult.correctAnswer;
    const isSelectedAnswer = answerText === state.answerResult.selectedAnswer;

    if (isCorrectAnswer) {
      // Bonne réponse - vert éclatant avec animation
      return {
        bg: "linear-gradient(135deg, #00FF88, #00CC6A)",
        color: "white",
        borderColor: "#00FF88",
        boxShadow: "0 0 20px rgba(0, 255, 136, 0.5)",
        transform: "scale(1.05)",
        fontWeight: "bold",
        _hover: {}
      };
    }

    if (isSelectedAnswer && !isCorrectAnswer) {
      // Mauvaise réponse sélectionnée - rouge vif avec animation
      return {
        bg: "linear-gradient(135deg, #FF4757, #FF3742)",
        color: "white",
        borderColor: "#FF4757",
        boxShadow: "0 0 20px rgba(255, 71, 87, 0.5)",
        transform: "scale(1.05)",
        fontWeight: "bold",
        _hover: {}
      };
    }

    // Autres réponses - style atténué
    return {
      bg: "var(--nuit-profonde)",
      color: "var(--texte-secondaire)",
      borderColor: "rgba(139, 148, 179, 0.1)",
      opacity: 0.6,
      _hover: {}
    };
  };

  if (!state.currentQuestion || !state.currentCategory) return null;

  // Ajouter des logs pour diagnostiquer la difficulté
  console.log('🎮 QuestionCard - currentQuestion:', state.currentQuestion);
  console.log('🎮 QuestionCard - imageUrl:', state.currentQuestion?.imageUrl);
  console.log('🎮 QuestionCard - difficulty:', state.currentQuestion.difficulty);
  console.log('🎮 QuestionCard - difficulty_level:', (state.currentQuestion as any).difficulty_level);

  // On récupère les infos de la catégorie depuis l'état du jeu pour le style
  const category = state.categories.find(c => c.id === state.currentCategory);
  if (!category) return null;

  const currentPlayer = state.players[state.currentPlayerIndex];
  const streak = currentPlayer.score[state.currentCategory] || 0;


  const handleAnswer = async (answer: string) => {
    if (selectedAnswer || state.answerResult) return; // Empêche de répondre plusieurs fois ou si un résultat est déjà affiché
    
    setSelectedAnswer(answer);
    
    // Son de sélection de réponse
    playSound('buttonClick');
    
    // Trouver l'index original de la réponse sélectionnée
    const originalIndex = getOriginalIndex(shuffledAnswers, answer);
    console.log('🎮 QuestionCard - Index original:', originalIndex);
    
    // On appelle la fonction du contexte et on attend le résultat (true ou false)
    const isCorrect = await answerQuestion(answer, originalIndex);
    
    // Jouer le son approprié selon le résultat
    if (isCorrect) {
      playSound('answerCorrect');
    } else {
      playSound('answerWrong');
    }
    
    // On ne fait plus de toast ni de nettoyage automatique ici
    // L'affichage du résultat est géré par l'état answerResult
  };

  return (
    <Box
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      maxW={cardWidth}
      w="full"
      p={{ base: 4, md: 6 }}
      bg="var(--gris-bleute)"
      borderRadius="xl"
      boxShadow="2xl"
      border="1px solid"
      borderColor="rgba(139, 148, 179, 0.2)"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: category.color,
      }}
    >
      <VStack spacing={{ base: 4, md: 6 }}>
        {/* Timer de réponse local */}
        {enableQuestionTimer && questionDeadline && (
          <Box w="full">
            <TimerBar
              deadline={questionDeadline}
              seconds={questionTimerSeconds}
              color={category.color}
              labelPrefix={t('game.answerTimer') || t('answerTimer') || 'Temps de réponse'}
            />
          </Box>
        )}
        <Flex
          direction="column"
          align="center"
          bg="var(--nuit-profonde)"
          p={4}
          borderRadius="lg"
          w="full"
        >
          <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="var(--texte-clair)">
              <PlayerName
                userId={auth.currentUser?.uid || ''}
                userName={currentPlayer.name}
                checkEffect={!!auth.currentUser?.uid}
              />
            </Text>
          <Flex align="center" gap={3} mb={2}>
            
            {(state.currentQuestion.difficulty || (state.currentQuestion as any).difficulty_level) && (
              <Badge 
                colorScheme={(() => {
                  const raw = Number(state.currentQuestion.difficulty || (state.currentQuestion as any).difficulty_level) || 1;
                  const v = raw > 5 ? Math.ceil(raw / 2) : raw; // map legacy 1..10 to 1..5
                  return v <= 2 ? 'green' : v <= 4 ? 'yellow' : 'red';
                })()}
                fontSize="xs"
              >
                {t('game.difficulty')} {(() => {
                  const raw = Number(state.currentQuestion.difficulty || (state.currentQuestion as any).difficulty_level) || 1;
                  const v = raw > 5 ? Math.ceil(raw / 2) : raw; // map legacy 1..10 to 1..5
                  const labels = ['Très facile','Facile','Moyen','Difficile','Très difficile'];
                  const clamp = (n: number) => Math.max(1, Math.min(5, Math.round(n)));
                  return labels[clamp(v)-1];
                })()}
              </Badge>
            )}
          </Flex>
          <Text 
            fontSize={{ base: "sm", md: "md" }} 
            color={category.color}
            fontWeight="bold"
          >
            {category.name}
            {!category.is_mystery && ` - ${t('streakLabel')}: ${streak}/${state.streakToWin}`}
          </Text>
        </Flex>
        
        <Text 
          fontSize={{ base: "lg", md: "xl" }} 
          fontWeight="bold" 
          textAlign="center"
          color="var(--texte-clair)"
          lineHeight="1.6"
        >
          {state.currentQuestion.question}
        </Text>

        {/* Affichage de l'image si présente */}
        {state.currentQuestion.imageUrl && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={4}
            mb={4}
          >
            <img
              src={state.currentQuestion.imageUrl}
              alt="Question illustration"
              style={{
                maxWidth: '300px',
                maxHeight: '200px',
                borderRadius: '8px',
                objectFit: 'contain',
                border: '2px solid var(--accent-color)',
              }}
              onError={(e) => {
                console.warn('❌ Image failed to load:', state.currentQuestion?.imageUrl);
                e.currentTarget.style.display = 'none';
              }}
              onLoad={() => {
                console.log('✅ Image loaded successfully:', state.currentQuestion?.imageUrl);
              }}
            />
          </Box>
        )}

        {state.answerResult && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              style={{ width: '100%' }}
            >
              <Flex
                direction="column"
                bg={state.answerResult.isCorrect ? "rgba(0, 255, 136, 0.1)" : "rgba(255, 71, 87, 0.1)"}
                border={`2px solid ${state.answerResult.isCorrect ? "#00FF88" : "#FF4757"}`}
                borderRadius="lg"
                p={4}
                align="center"
                gap={2}
              >
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color={state.answerResult.isCorrect ? "#00FF88" : "#FF4757"}
                >
                  {state.answerResult.isCorrect ? t('answerResult.correctTitle') : t('answerResult.incorrectTitle')}
                </Text>
                <Text color="var(--texte-clair)" fontSize="md">
                  {t('answerResult.correctAnswerLabel')} <strong>{state.answerResult.correctAnswer}</strong>
                </Text>
                {!state.answerResult.isCorrect && (
                  <Text color="var(--texte-secondaire)" fontSize="sm">
                    {t('answerResult.yourAnswerLabel')} {state.answerResult.selectedAnswer}
                  </Text>
                )}
                <Button
                  mt={2}
                  onClick={clearAnswerResult}
                  colorScheme="blue"
                  size="sm"
                >
                  {t('answerResult.continueButton')}
                </Button>
              </Flex>
            </motion.div>
          </AnimatePresence>
        )}

        <Grid 
          templateColumns={`repeat(${buttonColumns}, 1fr)`} 
          gap={4} 
          w="full"
          mt={4}
        >
          {shuffledAnswers.map((shuffledAnswer) => {
            const buttonStyle = getButtonStyle(shuffledAnswer.text);
            return (
              <Button
                key={shuffledAnswer.text}
                onClick={() => handleAnswer(shuffledAnswer.text)}
                size="lg"
                height={{ base: "60px", md: "auto" }}
                whiteSpace="normal"
                textAlign="center"
                p={4}
                isDisabled={selectedAnswer !== null || state.answerResult !== null}
                border="2px solid"
                borderRadius="md"
                transition="all 0.3s ease"
                {...buttonStyle}
              >
                {shuffledAnswer.text}
              </Button>
            );
          })}
        </Grid>
      </VStack>
    </Box>
  );
};

export default QuestionCard;